/**
 * InsightFundService — CRUD for the project-level Insight Fund at:
 *   projects/{pid}/insight_fund/{insightId}
 *
 * Items are typically promoted from a `research_runs/{runId}.analysis.*[N]`
 * source (Phase 3 keyFacts / contentSeeds / quotes / insightCandidates), or
 * authored manually. Consumed as additional grounding context in topic
 * generation and post generation (MasterPostHandler).
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit as fLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';

export interface InsightFundEntry {
  id: string;
  projectId: string;
  content: string;
  tags?: string[];
  source: {
    kind: 'research_run' | 'manual' | 'imported';
    runId?: string;
    fieldPath?: string; // e.g. "analysis.keyFacts[3]"
    chunkId?: number;
  };
  promotedAt?: string;
  promotedBy?: string;
  notes?: string;
  createdAt?: any;
  updatedAt?: any;
}

export type InsightFundCreate = Omit<InsightFundEntry, 'id' | 'createdAt' | 'updatedAt'>;
export type InsightFundPatch = Partial<Omit<InsightFundEntry, 'id' | 'projectId' | 'createdAt'>>;

export class InsightFundService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private collectionRef(projectId: string) {
    return collection(this.db, 'projects', projectId, 'insight_fund');
  }

  private docRef(projectId: string, insightId: string) {
    return doc(this.db, 'projects', projectId, 'insight_fund', insightId);
  }

  async create(projectId: string, entry: InsightFundCreate): Promise<string> {
    const stripped = stripUndefined({
      ...entry,
      projectId,
      promotedAt: entry.promotedAt ?? new Date().toISOString(),
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    const created = await addDoc(this.collectionRef(projectId), stripped);
    return created.id;
  }

  async update(projectId: string, insightId: string, patch: InsightFundPatch): Promise<void> {
    const stripped = stripUndefined({
      ...patch,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await updateDoc(this.docRef(projectId, insightId), stripped);
  }

  async delete(projectId: string, insightId: string): Promise<void> {
    await deleteDoc(this.docRef(projectId, insightId));
  }

  /**
   * One-shot list (no real-time). Use `subscribe` for live updates.
   */
  async list(projectId: string, opts?: { tag?: string; limit?: number }): Promise<InsightFundEntry[]> {
    const ref = this.collectionRef(projectId);
    let q = query(ref, orderBy('createdAt', 'desc'), fLimit(opts?.limit ?? 200));
    if (opts?.tag) {
      q = query(
        ref,
        where('tags', 'array-contains', opts.tag),
        orderBy('createdAt', 'desc'),
        fLimit(opts?.limit ?? 200),
      );
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ ...(d.data() as InsightFundEntry), id: d.id }));
  }

  /**
   * Real-time subscription. Returns unsubscribe fn.
   */
  subscribe(
    projectId: string,
    onNext: (items: InsightFundEntry[]) => void,
    onError?: (err: Error) => void,
  ): () => void {
    const q = query(
      this.collectionRef(projectId),
      orderBy('createdAt', 'desc'),
      fLimit(200),
    );
    return onSnapshot(
      q,
      (snap) => {
        onNext(snap.docs.map((d) => ({ ...(d.data() as InsightFundEntry), id: d.id })));
      },
      (err) => onError?.(err as Error),
    );
  }
}

export const insightFundService = new InsightFundService();
